import React, { useMemo } from 'react';
import { useMutation } from 'react-query';
import { Path, useFormContext, UseFormReturn } from 'react-hook-form';
import { DictionaryMdService } from 'dictionary_ts/v1/dictionary_web/dictionaries_md_pb_service';
import {
  ListProductRequest,
  ListProductResponse,
  ProductFilter
} from 'dictionary_ts/v1/dictionary_md/product_pb';
import { int64, string } from '@monorepo/helpers';

import { Product } from 'order_ts/v1/order/model_product_pb';
import { LookUpField, TFormField } from '@monorepo/forms';
import { getGrpcPromise, TGRPCPromiseReject } from '@monorepo/grpc';

type TProductField<TFormValues> = {
  valueKey: Path<TFormValues>;
  product: Product.AsObject;
} & TFormField<TFormValues>;

const BrandField = <
  TFormValues extends Record<string, any> = Record<string, any>
>({
  valueKey,
  product,
  ...props
}: TProductField<TFormValues>) => {
  const { setValue }: UseFormReturn<TFormValues> =
    useFormContext<TFormValues>();

  const handleSelect = (data: any) => {
    setValue(valueKey, data);
  };

  const searchMutator = (
    query: string
  ): Promise<ListProductResponse.AsObject> => {
    const request = new ListProductRequest();
    const filter = new ProductFilter();
    const { type, categoryId, process } = product;
    filter.setType(int64(type));
    filter.setProcess(int64(process));
    filter.setCategoryId(int64(categoryId));
    filter.setBrandName(string(query));
    request.setFilter(filter);

    return getGrpcPromise(DictionaryMdService.ListProduct)(request);
  };

  const { mutate, isLoading, data } = useMutation<
    ListProductResponse.AsObject,
    TGRPCPromiseReject,
    string
  >(searchMutator, { retry: false });

  const items = useMemo(
    () =>
      data?.itemsList?.length
        ? data.itemsList
            .map((item) => ({
              ...item.product,
              name: item.product?.brandName,
              productName: item.product?.name,
              quantityTypesList: item?.quantityTypesList
            }))
            .reduce(
              (arr, el) => (
                // eslint-disable-next-line no-sequences
                arr.find(({ name }) => el.name === name) || arr.push(el), arr
              ),
              []
            )
        : [],
    [data]
  );

  return (
    <LookUpField
      searchFc={mutate}
      isLoading={isLoading}
      items={items}
      onSelect={handleSelect}
      {...props}
    />
  );
};

export default BrandField;
